import toast from 'react-hot-toast';

/**
 * Globally dedupe react-hot-toast notifications by auto-injecting an `id` derived
 * from the message text when no id is passed. react-hot-toast treats identical
 * ids as "update existing" rather than "stack a new one", so two identical error
 * messages fired within the visible window collapse into a single toast.
 *
 * Patches toast.error / toast.success / toast.loading / toast.custom. The bare
 * `toast(msg)` callable form is not patched because the imported binding cannot
 * be reassigned across ES modules; bare usage is rare (mostly informational)
 * so the de-dup story is limited to the four typed methods.
 *
 * Callers can pass an explicit `id` to opt out (or to group different messages
 * under the same id intentionally).
 *
 * Non-string messages (JSX renderables) skip the auto-id since we can't compare
 * arbitrary React nodes for equality without false positives.
 */

type ToastType = 'error' | 'success' | 'loading' | 'custom';

const PATCHED_FLAG = '__scriptixDedupPatched';

const hashString = (str: string): string => {
  // FNV-1a 32-bit — fast, deterministic, fine for an id namespace.
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return `dt-${(hash >>> 0).toString(16)}`;
};

const isStringMessage = (msg: unknown): msg is string =>
  typeof msg === 'string';

const wrap = (type: ToastType) => {
  const original = toast[type].bind(toast);
  return (
    message: Parameters<typeof original>[0],
    opts?: Parameters<typeof original>[1]
  ) => {
    if (opts?.id || !isStringMessage(message)) {
      return original(message, opts);
    }
    return original(message, {
      ...opts,
      id: `${type}:${hashString(message)}`
    });
  };
};

const toastInternal = toast as unknown as Record<string, unknown>;

if (!toastInternal[PATCHED_FLAG]) {
  toast.error = wrap('error');
  toast.success = wrap('success');
  toast.loading = wrap('loading');
  toast.custom = wrap('custom');
  toastInternal[PATCHED_FLAG] = true;
}
